import type { OnePager } from '../types';
import { createClient, SupabaseClient } from '@supabase/supabase-js';

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

if (!supabaseUrl || !supabaseAnonKey) {
    console.error('Missing Supabase configuration. Please check your .env file.');
    console.error('VITE_SUPABASE_URL:', supabaseUrl);
    console.error('VITE_SUPABASE_ANON_KEY:', supabaseAnonKey ? 'Present' : 'Missing');
}

class ApiService {
    private supabase: SupabaseClient;
    private usernameCache: Map<string, { available: boolean; timestamp: number }>;
    private readonly CACHE_DURATION = 60000; // 1 minute cache

    constructor() {
        if (!supabaseUrl || !supabaseAnonKey) {
            throw new Error('Supabase configuration missing. Please add VITE_SUPABASE_URL and VITE_SUPABASE_ANON_KEY to your .env file.');
        }
        this.supabase = createClient(supabaseUrl, supabaseAnonKey);
        this.usernameCache = new Map();
    }

    // Auth endpoints
    async register(userData: { email: string; password: string; full_name: string; username: string }) {
        // Sign up with Supabase Auth (username already validated in signup form)
        const { data: authData, error: signUpError } = await this.supabase.auth.signUp({
            email: userData.email,
            password: userData.password,
            options: {
                emailRedirectTo: undefined, // Disable email confirmation for development
                data: {
                    full_name: userData.full_name,
                    username: userData.username.toLowerCase()
                }
            }
        });

        if (signUpError) throw new Error(signUpError.message);

        // Insert user profile
        if (authData.user) {
            const { error: profileError } = await this.supabase
                .from('users')
                .insert({
                    id: authData.user.id,
                    email: userData.email,
                    username: userData.username.toLowerCase(),
                    full_name: userData.full_name
                });

            if (profileError) throw new Error(profileError.message);
        }

        return { message: 'User created successfully', user: authData.user };
    }

    async checkUsername(username: string): Promise<{ available: boolean; username: string }> {
        const normalizedUsername = username.toLowerCase();
        
        // Check cache first
        const cached = this.usernameCache.get(normalizedUsername);
        if (cached && Date.now() - cached.timestamp < this.CACHE_DURATION) {
            return {
                available: cached.available,
                username: normalizedUsername
            };
        }

        // Query Supabase - use maybeSingle() to avoid 406 error when no match
        const { data, error } = await this.supabase
            .from('users')
            .select('id')
            .eq('username', normalizedUsername)
            .maybeSingle();

        if (error) {
            throw new Error(error.message);
        }

        const available = !data;
        
        // Cache the result
        this.usernameCache.set(normalizedUsername, {
            available,
            timestamp: Date.now()
        });

        return {
            available,
            username: normalizedUsername
        };
    }

    async login(credentials: { email: string; password: string }) {
        const { data, error } = await this.supabase.auth.signInWithPassword({
            email: credentials.email,
            password: credentials.password
        });

        if (error) throw new Error(error.message);

        // Get user profile
        const { data: profile } = await this.supabase
            .from('users')
            .select('*')
            .eq('id', data.user?.id)
            .single();

        return {
            message: 'Login successful',
            user: profile,
            token: data.session?.access_token
        };
    }

    logout() {
        return this.supabase.auth.signOut();
    }

    // User profile endpoints
    async getUserProfile() {
        const { data: { user } } = await this.supabase.auth.getUser();
        
        if (!user) throw new Error('Not authenticated');

        const { data, error } = await this.supabase
            .from('users')
            .select('*')
            .eq('id', user.id)
            .single();

        if (error) throw new Error(error.message);

        return data;
    }

    async updateUserProfile(userData: { full_name: string; username: string }) {
        const { data: { user } } = await this.supabase.auth.getUser();
        
        if (!user) throw new Error('Not authenticated');

        // Check if username is taken by another user
        const { data: existingUser } = await this.supabase
            .from('users')
            .select('id')
            .eq('username', userData.username.toLowerCase())
            .neq('id', user.id)
            .maybeSingle();

        if (existingUser) {
            throw new Error('Username is already taken');
        }

        const { data, error } = await this.supabase
            .from('users')
            .update({
                full_name: userData.full_name,
                username: userData.username.toLowerCase(),
                updated_at: new Date().toISOString()
            })
            .eq('id', user.id)
            .select()
            .single();

        if (error) throw new Error(error.message);

        return { message: 'Profile updated successfully', user: data };
    }

    async updateUserPassword(newPassword: string) {
        const { error } = await this.supabase.auth.updateUser({ password: newPassword });
        if (error) throw new Error(error.message);
        return true;
    }

    // One-pager endpoints
    async getAllOnePagers() {
        const { data, error } = await this.supabase
            .from('one_pagers')
            .select(`
                *,
                clone_count,
                build_count,
                creator:users!creator_id(id, email, full_name, username)
            `)
            .order('created_at', { ascending: false });

        if (error) throw new Error(error.message);

        return data.map(item => ({
            ...item,
            spec: typeof item.spec === 'string' ? JSON.parse(item.spec) : item.spec,
            creator: item.creator ? {
                id: item.creator.id,
                email: item.creator.email,
                fullName: item.creator.full_name,
                username: item.creator.username
            } : undefined
        }));
    }

    async getTemplates() {
        const { data, error } = await this.supabase
            .from('one_pagers')
            .select(`
                *,
                creator:users!creator_id(id, email, full_name, username)
            `)
            .eq('is_template', true)
            .order('created_at', { ascending: false });

        if (error) throw new Error(error.message);

        return data.map(item => ({
            ...item,
            spec: typeof item.spec === 'string' ? JSON.parse(item.spec) : item.spec,
            creator: item.creator ? {
                id: item.creator.id,
                email: item.creator.email,
                fullName: item.creator.full_name,
                username: item.creator.username
            } : undefined
        }));
    }

    async getOnePagerById(id: string): Promise<OnePager> {
        const { data, error } = await this.supabase
            .from('one_pagers')
            .select(`
                *,
                creator:users!creator_id(id, email, full_name, username)
            `)
            .eq('id', id)
            .single();

        if (error) throw new Error(error.message);

        return {
            ...data,
            spec: typeof data.spec === 'string' ? JSON.parse(data.spec) : data.spec,
            creator: data.creator ? {
                id: data.creator.id,
                email: data.creator.email,
                fullName: data.creator.full_name,
                username: data.creator.username
            } : undefined
        };
    }

    async createOnePager(data: {
        id?: string;
        title: string;
        description?: string;
        content: string;
        spec: any;
        is_template?: boolean;
    }): Promise<OnePager> {
        const { data: { user } } = await this.supabase.auth.getUser();
        
        if (!user) throw new Error('Not authenticated');

        const insertData: any = {
            title: data.title,
            description: data.description || '',
            content: data.content,
            spec: data.spec,
            creator_id: user.id,
            is_template: data.is_template || false
        };
        if (data.id) insertData.id = data.id;

        const { data: onePager, error } = await this.supabase
            .from('one_pagers')
            .insert(insertData)
            .select(`
                *,
                creator:users!creator_id(id, email, full_name, username)
            `)
            .single();

        if (error) throw new Error(error.message);

        return {
            ...onePager,
            spec: typeof onePager.spec === 'string' ? JSON.parse(onePager.spec) : onePager.spec,
            creator: onePager.creator ? {
                id: onePager.creator.id,
                email: onePager.creator.email,
                fullName: onePager.creator.full_name,
                username: onePager.creator.username
            } : undefined
        };
    }

    async updateOnePager(id: string, data: {
        title?: string;
        description?: string;
        content?: string;
        spec?: any;
        is_template?: boolean;
    }): Promise<OnePager> {
        const { data: { user } } = await this.supabase.auth.getUser();
        
        if (!user) throw new Error('Not authenticated');

        const updateData: any = {
            updated_at: new Date().toISOString()
        };

        if (data.title !== undefined) updateData.title = data.title;
        if (data.description !== undefined) updateData.description = data.description;
        if (data.content !== undefined) updateData.content = data.content;
        if (data.spec !== undefined) updateData.spec = data.spec;
        if (data.is_template !== undefined) updateData.is_template = data.is_template;

        const { data: onePager, error } = await this.supabase
            .from('one_pagers')
            .update(updateData)
            .eq('id', id)
            .eq('creator_id', user.id)
            .select(`
                *,
                creator:users!creator_id(id, email, full_name, username)
            `)
            .single();

        if (error) throw new Error(error.message);

        return {
            ...onePager,
            spec: typeof onePager.spec === 'string' ? JSON.parse(onePager.spec) : onePager.spec,
            creator: onePager.creator ? {
                id: onePager.creator.id,
                email: onePager.creator.email,
                fullName: onePager.creator.full_name,
                username: onePager.creator.username
            } : undefined
        };
    }

    async deleteOnePager(id: string) {
        const { data: { user } } = await this.supabase.auth.getUser();
        
        if (!user) throw new Error('Not authenticated');

        const { error } = await this.supabase
            .from('one_pagers')
            .delete()
            .eq('id', id)
            .eq('creator_id', user.id);

        if (error) throw new Error(error.message);
    }

    async createFromTemplate(templateId: string, customizations?: {
        title?: string;
        description?: string;
        content?: string;
        spec?: any;
    }) {
        const { data: { user } } = await this.supabase.auth.getUser();
        
        if (!user) throw new Error('Not authenticated');

        // Get template
        const { data: template, error: fetchError } = await this.supabase
            .from('one_pagers')
            .select('*')
            .eq('id', templateId)
            .single();

        if (fetchError) throw new Error(fetchError.message);

        // Create new one-pager from template
        // Generate a new 6-char alphanumeric ID for the copy
        const generateId = () => {
            const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
            let result = '';
            for (let i = 0; i < 6; i++) {
                result += chars.charAt(Math.floor(Math.random() * chars.length));
            }
            return result;
        };
        const newId = generateId();
        const { data: onePager, error: createError } = await this.supabase
            .from('one_pagers')
            .insert({
                id: newId,
                title: customizations?.title || `${template.title} (Copy)`,
                description: customizations?.description || template.description,
                content: customizations?.content || template.content,
                spec: customizations?.spec || template.spec,
                creator_id: user.id,
                is_template: false
            })
            .select(`
                *,
                creator:users!creator_id(id, email, full_name, username)
            `)
            .single();

        if (createError) throw new Error(createError.message);

        return onePager;
    }

    async getUserOnePagers() {
        const { data: { user } } = await this.supabase.auth.getUser();
        
        if (!user) throw new Error('Not authenticated');

        const { data, error } = await this.supabase
            .from('one_pagers')
            .select(`
                *,
                creator:users!creator_id(id, email, full_name, username)
            `)
            .eq('creator_id', user.id)
            .order('created_at', { ascending: false });

        if (error) throw new Error(error.message);

        return data.map(item => ({
            ...item,
            spec: typeof item.spec === 'string' ? JSON.parse(item.spec) : item.spec,
            creator: item.creator ? {
                id: item.creator.id,
                email: item.creator.email,
                fullName: item.creator.full_name,
                username: item.creator.username
            } : undefined
        }));
    }

    async getUserByUsername(username: string) {
        const { data, error } = await this.supabase
            .from('users')
            .select('id, email, full_name, username')
            .eq('username', username.toLowerCase())
            .maybeSingle();

        if (error) throw new Error(error.message);
        if (!data) throw new Error('User not found');

        return {
            id: data.id,
            email: data.email,
            fullName: data.full_name,
            username: data.username
        };
    }

    async getOnePagersByUsername(username: string) {
        // First get the user
        const user = await this.getUserByUsername(username);

        const { data, error } = await this.supabase
            .from('one_pagers')
            .select(`
                *,
                clone_count,
                build_count,
                creator:users!creator_id(id, email, full_name, username)
            `)
            .eq('creator_id', user.id)
            .order('created_at', { ascending: false });

        if (error) throw new Error(error.message);

        return data.map(item => ({
            ...item,
            spec: typeof item.spec === 'string' ? JSON.parse(item.spec) : item.spec,
            creator: item.creator ? {
                id: item.creator.id,
                email: item.creator.email,
                fullName: item.creator.full_name,
                username: item.creator.username
            } : undefined
        }));
    }

    async incrementCloneCount(onePagerId: number) {
        // Get current user
        const { data: { user } } = await this.supabase.auth.getUser();
        if (!user) return; // Only count for authenticated users

        // Get one-pager to check creator
        const { data: onePager } = await this.supabase
            .from('one_pagers')
            .select('creator_id, authorId, clone_count')
            .eq('id', onePagerId)
            .single();
        if (!onePager) return;

        // Don't count self-clone
        if (user.id === onePager.creator_id?.toString() || user.id === onePager.authorId) return;

        // Check if user already cloned this one-pager
        const { data: existingClone } = await this.supabase
            .from('clones')
            .select('id')
            .eq('user_id', user.id)
            .eq('one_pager_id', onePagerId)
            .maybeSingle();
        if (existingClone) return; // Already counted

        // Insert clone record
        await this.supabase
            .from('clones')
            .insert({ user_id: user.id, one_pager_id: onePagerId });

        // Increment clone count
        await this.supabase
            .from('one_pagers')
            .update({ clone_count: (onePager.clone_count || 0) + 1 })
            .eq('id', onePagerId);
    }

    async incrementBuildCount(onePagerId: number) {
        const { error } = await this.supabase.rpc('increment_build_count', { 
            one_pager_id: onePagerId 
        });

        if (error) {
            // Fallback to manual increment if function doesn't exist
            const { data: onePager } = await this.supabase
                .from('one_pagers')
                .select('build_count')
                .eq('id', onePagerId)
                .single();

            if (onePager) {
                await this.supabase
                    .from('one_pagers')
                    .update({ build_count: (onePager.build_count || 0) + 1 })
                    .eq('id', onePagerId);
            }
        }
    }

    async getUserMetrics(username: string) {
        const user = await this.getUserByUsername(username);

        const { data, error } = await this.supabase
            .from('one_pagers')
            .select('clone_count, build_count')
            .eq('creator_id', user.id);

        if (error) throw new Error(error.message);

        const totalClones = data.reduce((sum, item) => sum + (item.clone_count || 0), 0);
        const totalBuilds = data.reduce((sum, item) => sum + (item.build_count || 0), 0);

        return {
            totalClones,
            totalBuilds,
            totalOnePagers: data.length
        };
    }

    async sendPasswordResetEmail(email: string) {
        const { error } = await this.supabase.auth.resetPasswordForEmail(email, {
          redirectTo: window.location.origin + '/reset-password',
        });
        if (error) throw new Error(error.message);
        return true;
      }
}

export const apiService = new ApiService();
export default apiService;